import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Route, Redirect, Switch } from 'react-router-dom';

import Users from './users/pages/Users';
import PrescriptionPreviewPage from './users/components/PrescriptionPreviewPage';
import UpdatePatient from './information/pages/UpdatePatient';
import Reports from './information/pages/Reports';
import PatientHistory from './information/pages/PatientHistory';
import Patient from './information/pages/Patient';
import DoctorProfile from './users/pages/DoctorProfile';
import Auth from './users/pages/Auth';
import MainNavigation from './shared/components/Navigation/MainNavigation';
import {
  NavigationMemory,
  BackNavigation,
  RouteContent
} from './shared/components/Navigation/BackNavigation';
import LoadingSpinner from './shared/components/UIElements/LoadingSpinner';
import { clearSessionScope, readAuthSession } from './shared/util/session-storage';
import { AuthContext } from './shared/Context/auth-context';
import PatientProfile from './users/pages/PatientProfile';
import PharmacyDashboard from './users/pages/PharmacyDashboard';
import PharmacyPrescriptions from './users/components/PharmacyPrescription';
import PharmacyCRM from './users/pages/PharmacyAlert';
import PharmacyInventoryDashboard from './users/pages/PharmacyInsight';
import CareTimeline from './care/CareTimeline';
import CareTeam from './care/CareTeam';
import CareInbox from './care/CareInbox';

let logoutDTimer;
let logoutPTimer;
let logoutPhTimer;

const App = () => {
  const [authReady, setAuthReady] = useState(false);
  const [token, setToken] = useState(false);
  const [doctortokenExpirationDate, setDoctortokenExpirationDate] = useState();
  const [isDoctorLoggedIn, setIsDoctorLoggedIn] = useState(false);
  const [doctorId, setDoctorId] = useState(null);
  const [doctor, setDoctor] = useState(null); // ✅ Store full doctor object

  const Dlogin = useCallback((doctorData, token, expirationDate) => {
    setToken(token);
    setIsDoctorLoggedIn(true);
    setDoctorId(doctorData._id);
    setDoctor(doctorData); // ✅ Store full doctor object
    const doctortokenExpirationDate =
      expirationDate || new Date(new Date().getTime() + 1000 * 60 * 60);
    setDoctortokenExpirationDate(doctortokenExpirationDate);
    localStorage.setItem(
      'doctorData',
      JSON.stringify({
        user: doctorData, // store the whole user object
        token: token,
        expiration: doctortokenExpirationDate.toISOString()
      })
    );
  }, []);

  const Dlogout = useCallback(() => {
    setToken(null);
    setIsDoctorLoggedIn(false);
    setDoctorId(null);
    setDoctor(null); // ✅ Clear on logout
    localStorage.removeItem('doctorData');
    clearSessionScope();
  }, []);

  useEffect(() => {
    if (token && doctortokenExpirationDate) {
      const remainingTime = doctortokenExpirationDate.getTime() - new Date().getTime();
      logoutDTimer = setTimeout(Dlogout, remainingTime);
    } else {
      clearTimeout(logoutDTimer);
    }
  }, [token, Dlogout, doctortokenExpirationDate]);

  useEffect(() => {
    const storedData = readAuthSession('doctorData');
    if (
      storedData &&
      storedData.token &&
      storedData.user &&
      new Date(storedData.expiration) > new Date()
    ) {
      Dlogin(storedData.user, storedData.token, new Date(storedData.expiration));
    }
  }, [Dlogin]);

  const [isPateintLoggedIn, setIsPateintLoggedIn] = useState(false);
  const [patientId, setPatientId] = useState(null);
  const [patient, setPatient] = useState(null); // ✅ Store full patient object
  const [patientTokenExpirationDate, setPatienttokenExpirationDate] = useState();

  const Plogin = useCallback((patientData, token, expirationPDate) => {
    setToken(token);
    setIsPateintLoggedIn(true);
    setPatientId(patientData._id);
    setPatient(patientData); // ✅ Store full patient object
    const patientTokenExpirationDate =
      expirationPDate || new Date(new Date().getTime() + 1000 * 60 * 60);
    setPatienttokenExpirationDate(patientTokenExpirationDate);
    localStorage.setItem(
      'patientData',
      JSON.stringify({
        user: patientData,
        token: token,
        expiration: patientTokenExpirationDate.toISOString()
      })
    );
  }, []);

  const Plogout = useCallback(() => {
    setToken(null);
    setIsPateintLoggedIn(false);
    setPatientId(null);
    setPatient(null); // ✅ Clear on logout
    localStorage.removeItem('patientData');
    clearSessionScope();
  }, []);

  useEffect(() => {
    if (token && patientTokenExpirationDate) {
      const remainingTime = patientTokenExpirationDate.getTime() - new Date().getTime();
      logoutPTimer = setTimeout(Plogout, remainingTime);
    } else {
      clearTimeout(logoutPTimer);
    }
  }, [token, Plogout, patientTokenExpirationDate]);

  useEffect(() => {
    const storedData = readAuthSession('patientData');
    if (
      storedData &&
      storedData.token &&
      storedData.user &&
      new Date(storedData.expiration) > new Date()
    ) {
      Plogin(storedData.user, storedData.token, new Date(storedData.expiration));
    }
  }, [Plogin]);

  const [isPharmacyLoggedIn, setIsPharmacyLoggedIn] = useState(false);
  const [pharmacyId, setPharmacyId] = useState(null);
  const [pharmacy, setPharmacy] = useState(null);
  const [pharmacyTokenExpirationDate, setPharmacyTokenExpirationDate] = useState();

  const Phlogin = useCallback((pharmacyData, token, expirationPhDate) => {
    setToken(token);
    setIsPharmacyLoggedIn(true);
    setPharmacyId(pharmacyData._id);
    setPharmacy(pharmacyData);
    const tokenExpiry = expirationPhDate || new Date(new Date().getTime() + 1000 * 60 * 60);
    setPharmacyTokenExpirationDate(tokenExpiry);

    localStorage.setItem(
      'pharmacyData',
      JSON.stringify({
        user: pharmacyData,
        token: token,
        expiration: tokenExpiry.toISOString()
      })
    );
  }, []);

  const Phlogout = useCallback(() => {
    setToken(null);
    setIsPharmacyLoggedIn(false);
    setPharmacyId(null);
    setPharmacy(null);
    localStorage.removeItem('pharmacyData');
    clearSessionScope();
  }, []);

  useEffect(() => {
    if (token && pharmacyTokenExpirationDate) {
      const remainingTime = pharmacyTokenExpirationDate.getTime() - new Date().getTime();
      logoutPhTimer = setTimeout(Phlogout, remainingTime);
    } else {
      clearTimeout(logoutPhTimer);
    }
  }, [token, Phlogout, pharmacyTokenExpirationDate]);

  useEffect(() => {
    const storedData = readAuthSession('pharmacyData');
    if (
      storedData &&
      storedData.token &&
      storedData.user &&
      new Date(storedData.expiration) > new Date()
    ) {
      Phlogin(storedData.user, storedData.token, new Date(storedData.expiration));
    }
  }, [Phlogin]);

  useEffect(() => {
    setAuthReady(true);
  }, []);

  const scope = isDoctorLoggedIn
    ? `doctor:${doctorId}`
    : isPateintLoggedIn
      ? `patient:${patientId}`
      : isPharmacyLoggedIn
        ? `pharmacy:${pharmacyId}`
        : 'guest';
  const updateDoctor = useCallback((updated) => {
    setDoctor(updated);
    const saved = readAuthSession('doctorData');
    try {
      if (saved) localStorage.setItem('doctorData', JSON.stringify({ ...saved, user: updated }));
    } catch {}
  }, []);
  const updatePharmacy = useCallback((updated) => {
    setPharmacy(updated);
    const saved = readAuthSession('pharmacyData');
    try {
      if (saved) localStorage.setItem('pharmacyData', JSON.stringify({ ...saved, user: updated }));
    } catch {}
  }, []);
  let routes;

  if (isDoctorLoggedIn) {
    routes = (
      <Switch>
        <Route path="/care/inbox" exact>
          <CareInbox />
        </Route>
        <Route path="/care/patients/:patientId" exact>
          <CareTimeline />
        </Route>
        <Route path="/" exact>
          <DoctorProfile />
        </Route>
        <Route path="/:doctorId/search">
          <Patient />
        </Route>
        <Route path="/:doctorId/prescribe/:patientId">
          <UpdatePatient />
        </Route>
        <Route path="/reports/:patientId" component={Reports} />
        <Route path="/preview/:prescriptionId" exact>
          <PrescriptionPreviewPage />
        </Route>
        <Redirect to="/" />
      </Switch>
    );
  } else if (isPateintLoggedIn) {
    routes = (
      <Switch>
        <Route path="/care/team" exact>
          <CareTeam />
        </Route>
        <Route path="/care" exact>
          <CareTimeline />
        </Route>
        <Route path="/" exact>
          <PatientProfile />
        </Route>
        <Route path="/:patientId/history" exact>
          <PatientHistory />
        </Route>
        <Route path="/reports/:patientId">
          <Reports />
        </Route>
        <Redirect to="/" />
      </Switch>
    );
  } else if (isPharmacyLoggedIn) {
    routes = (
      <Switch>
        <Route path="/care/inbox" exact>
          <CareInbox />
        </Route>
        <Route path="/care/patients/:patientId" exact>
          <CareTimeline />
        </Route>
        <Route path="/" exact>
          <PharmacyDashboard />
        </Route>
        <Route path="/:pharmacyId/prescriptions" exact>
          <PharmacyPrescriptions />
        </Route>
        <Route path="/:pharmacyId/CRM" exact>
          <PharmacyCRM />
        </Route>
        <Route path="/:pharmacyId/analysis" exact>
          <PharmacyInventoryDashboard />
        </Route>
        <Redirect to="/" />
      </Switch>
    );
  } else {
    routes = (
      <Switch>
        <Route path="/" exact>
          <Users />
        </Route>
        <Route path="/auth/:userType" exact>
          <Auth />
        </Route>
        <Redirect to="/" />
      </Switch>
    );
  }

  // Resolve saved authentication before mounting routes: otherwise a deep-link
  // refresh briefly renders guest routes and redirects away from the current screen.
  if (!authReady) return <LoadingSpinner asOverlay />;

  return (
    <AuthContext.Provider
      value={{
        token,
        isDoctorLoggedIn,
        isPateintLoggedIn,
        isPharmacyLoggedIn,
        doctor,
        patient,
        pharmacy,
        doctorId,
        patientId,
        pharmacyId,
        Plogin,
        Plogout,
        Dlogin,
        Dlogout,
        Phlogin,
        Phlogout,
        updateDoctor,
        setPharmacy: updatePharmacy
      }}
    >
      <Router>
        <NavigationMemory scope={scope} key={scope}>
          <div
            className={
              isDoctorLoggedIn || isPateintLoggedIn || isPharmacyLoggedIn
                ? 'app-shell app-shell--workspace'
                : 'app-shell app-shell--guest'
            }
          >
            <MainNavigation />
            <main id="main-content" tabIndex="-1">
              <BackNavigation doctorId={isDoctorLoggedIn ? doctorId : null} />
              <RouteContent>{routes}</RouteContent>
            </main>
          </div>
        </NavigationMemory>
      </Router>
    </AuthContext.Provider>
  );
};

export default App;
